import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { fiscalYearLabel } from "@/features/purchase/constants";
import {
  computeLineAmounts,
  computePoTotals,
  type ComputedLine,
} from "@/features/purchase/api";
import type { SalesOrderFormValues } from "@/features/sales-orders/schema";
import type {
  SalesOrder,
  SalesOrderLine,
  SalesOrderLineTax,
  SalesOrderListRow,
  SalesOrderStatus,
} from "@/features/sales-orders/types";
import type { TaxRate } from "@/features/settings/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrateSo(raw: SalesOrder): SalesOrder {
  return {
    ...raw,
    subtotal: money(raw.subtotal),
    discount: money(raw.discount),
    tax_total: money(raw.tax_total),
    adjustment: money(raw.adjustment),
    grand_total: money(raw.grand_total),
  };
}

function hydrateLine(raw: SalesOrderLine): SalesOrderLine {
  return {
    ...raw,
    qty: money(raw.qty),
    weight: money(raw.weight),
    unit_rate: money(raw.unit_rate),
    line_subtotal: money(raw.line_subtotal),
    gst_slab: money(raw.gst_slab),
    fulfilled_qty: money(raw.fulfilled_qty),
    fulfilled_weight: money(raw.fulfilled_weight),
    taxes: (raw.taxes ?? []).map((t) => ({
      ...t,
      rate: money(t.rate),
      amount: money(t.amount),
    })),
  };
}

async function nextSoNumber(issueDate: string): Promise<string> {
  const { data: company } = await supabase
    .from("company")
    .select("so_prefix, fiscal_year_start_month")
    .limit(1)
    .maybeSingle();
  const prefix = ((company?.so_prefix as string | undefined) ?? "SO").trim().toUpperCase() || "SO";
  const fy = fiscalYearLabel(issueDate, Number(company?.fiscal_year_start_month) || 4);
  const pattern = `${prefix}-${fy}-`;
  const { data, error } = await supabase
    .from("sales_orders")
    .select("so_number")
    .ilike("so_number", `${pattern}%`)
    .order("so_number", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.so_number as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  const next = (Number.isNaN(n) ? 0 : n) + 1;
  return `${pattern}${String(next).padStart(4, "0")}`;
}

async function loadClientPos(clientId: string) {
  const { data, error } = await supabase
    .from("clients")
    .select("place_of_supply, state, payment_terms, name, code, addr1, city, pincode")
    .eq("id", clientId)
    .single();
  if (error) throw error;
  return data as {
    place_of_supply: string | null;
    state: string;
    payment_terms: string;
    name: string;
    code: string;
    addr1: string;
    city: string;
    pincode: string;
  };
}

async function loadCompanyState() {
  const { data } = await supabase
    .from("company")
    .select("state, name, addr1, city, pincode")
    .limit(1)
    .maybeSingle();
  return data as {
    state: string | null;
    name: string | null;
    addr1: string | null;
    city: string | null;
    pincode: string | null;
  } | null;
}

async function loadTaxRates(): Promise<TaxRate[]> {
  const { data, error } = await supabase.from("tax_rates").select("*").eq("is_active", true);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    ...(r as TaxRate),
    rate: money((r as TaxRate).rate),
    gst_slab:
      (r as TaxRate).gst_slab === null || (r as TaxRate).gst_slab === undefined
        ? null
        : money((r as TaxRate).gst_slab),
  }));
}

async function replaceLines(
  soId: string,
  values: SalesOrderFormValues,
  rates: TaxRate[],
  companyState: string | null | undefined,
  clientPos: string | null | undefined,
) {
  const { error: delErr } = await supabase.from("sales_order_lines").delete().eq("sales_order_id", soId);
  if (delErr) throw delErr;

  const computedLines: ComputedLine[] = [];
  for (let i = 0; i < values.lines.length; i++) {
    const line = values.lines[i];
    const computed = computeLineAmounts(
      line,
      rates,
      companyState,
      clientPos,
      values.issue_date,
    );
    computedLines.push(computed);

    const { data: inserted, error: lineErr } = await supabase
      .from("sales_order_lines")
      .insert({
        sales_order_id: soId,
        line_no: i + 1,
        material_id: line.material_id,
        material_grade_id: text(line.material_grade_id),
        shape_size_id: text(line.shape_size_id),
        description: text(line.description),
        qty: line.qty,
        weight: line.weight,
        uom_id: text(line.uom_id),
        unit_rate: line.unit_rate,
        line_subtotal: computed.line_subtotal,
        gst_slab: line.gst_slab,
        fulfilled_qty: 0,
        fulfilled_weight: 0,
      })
      .select("id")
      .single();
    if (lineErr) throw lineErr;

    if (computed.taxes.length) {
      const { error: taxErr } = await supabase.from("sales_order_line_taxes").insert(
        computed.taxes.map((t) => ({
          line_id: inserted.id,
          tax_rate_id: t.tax_rate_id,
          tax_type: t.tax_type,
          rate: t.rate,
          amount: t.amount,
        })),
      );
      if (taxErr) throw taxErr;
    }
  }

  return computePoTotals(computedLines, values.discount, values.adjustment);
}

export function salesOrderToForm(so: SalesOrder): SalesOrderFormValues {
  return {
    client_id: so.client_id,
    quotation_id: so.quotation_id ?? "",
    client_po_ref: so.client_po_ref ?? "",
    issue_date: so.issue_date,
    due_date: so.due_date ?? "",
    payment_terms: so.payment_terms ?? "",
    bill_to_text: so.bill_to_text ?? "",
    ship_to_text: so.ship_to_text ?? "",
    discount: so.discount,
    adjustment: so.adjustment,
    notes: so.notes ?? "",
    terms: so.terms ?? "",
    lines: (so.lines ?? []).map((line) => ({
      id: line.id,
      material_id: line.material_id,
      material_grade_id: line.material_grade_id ?? "",
      shape_size_id: line.shape_size_id ?? "",
      description: line.description ?? "",
      qty: line.qty,
      weight: line.weight,
      uom_id: line.uom_id ?? "",
      unit_rate: line.unit_rate,
      gst_slab: line.gst_slab,
    })),
  };
}

export function useSalesOrders(filters?: { clientId?: string }) {
  return useQuery({
    queryKey: ["sales_orders", filters?.clientId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<SalesOrderListRow[]> => {
      let q = supabase
        .from("sales_orders")
        .select("*, clients(code, name), quotations(qt_number), sales_order_lines(id)")
        .order("issue_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (filters?.clientId) q = q.eq("client_id", filters.clientId);
      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const client = raw.clients as { code?: string; name?: string } | null;
        const qt = raw.quotations as { qt_number?: string } | null;
        const lines = raw.sales_order_lines as { id: string }[] | null;
        const { clients: _c, quotations: _q, sales_order_lines: _l, ...rest } = raw;
        return {
          ...hydrateSo(rest as unknown as SalesOrder),
          client_code: client?.code ?? null,
          client_name: client?.name ?? null,
          qt_number: qt?.qt_number ?? null,
          line_count: lines?.length ?? 0,
        };
      });
    },
  });
}

export function useSalesOrder(id?: string) {
  return useQuery({
    queryKey: ["sales_orders", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<SalesOrder> => {
      const { data, error } = await supabase
        .from("sales_orders")
        .select(
          `*,
          clients(code, name, place_of_supply, state),
          quotations(qt_number),
          sales_order_lines(
            *,
            sales_order_line_taxes(*),
            materials(name),
            material_grades(name),
            shape_sizes(size_label),
            uoms(code)
          )`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const client = raw.clients as { code?: string; name?: string } | null;
      const qt = raw.quotations as { qt_number?: string } | null;
      const linesRaw = (raw.sales_order_lines as Record<string, unknown>[] | null) ?? [];
      const { clients: _c, quotations: _q, sales_order_lines: _l, ...rest } = raw;

      const lines = linesRaw
        .map((line) => {
          const taxes = (
            (line.sales_order_line_taxes as SalesOrderLineTax[] | null) ?? []
          ).map((t) => ({ ...t, rate: money(t.rate), amount: money(t.amount) }));
          const material = line.materials as { name?: string } | null;
          const grade = line.material_grades as { name?: string } | null;
          const shape = line.shape_sizes as { size_label?: string } | null;
          const uom = line.uoms as { code?: string } | null;
          const {
            sales_order_line_taxes: _t,
            materials: _m,
            material_grades: _g,
            shape_sizes: _s,
            uoms: _u,
            ...lineRest
          } = line;
          return hydrateLine({
            ...(lineRest as unknown as SalesOrderLine),
            taxes,
            material_name: material?.name ?? null,
            grade_name: grade?.name ?? null,
            shape_label: shape?.size_label ?? null,
            uom_code: uom?.code ?? null,
          });
        })
        .sort((a, b) => a.line_no - b.line_no);

      return {
        ...hydrateSo(rest as unknown as SalesOrder),
        client_code: client?.code ?? null,
        client_name: client?.name ?? null,
        qt_number: qt?.qt_number ?? null,
        lines,
      };
    },
  });
}

async function insertSalesOrder(
  values: SalesOrderFormValues,
  opts: { status: SalesOrderStatus; userId: string | undefined },
) {
  const [client, company, rates] = await Promise.all([
    loadClientPos(values.client_id),
    loadCompanyState(),
    loadTaxRates(),
  ]);
  const soNumber = await nextSoNumber(values.issue_date);
  const billTo =
    text(values.bill_to_text) ??
    [client.name, client.addr1, client.city, client.pincode].filter(Boolean).join("\n");

  const { data: header, error } = await supabase
    .from("sales_orders")
    .insert({
      so_number: soNumber,
      client_id: values.client_id,
      quotation_id: text(values.quotation_id),
      client_po_ref: text(values.client_po_ref),
      issue_date: values.issue_date,
      due_date: text(values.due_date),
      status: opts.status,
      bill_to_text: billTo,
      ship_to_text: text(values.ship_to_text),
      payment_terms: text(values.payment_terms) ?? client.payment_terms,
      notes: text(values.notes),
      terms: text(values.terms),
      discount: values.discount,
      adjustment: values.adjustment,
      created_by: opts.userId ?? null,
      updated_by: opts.userId ?? null,
    })
    .select("*")
    .single();
  if (error) throw error;

  const clientPos = client.place_of_supply || client.state;
  const totals = await replaceLines(header.id, values, rates, company?.state, clientPos);

  const { data: updated, error: updErr } = await supabase
    .from("sales_orders")
    .update({ ...totals, updated_at: new Date().toISOString() })
    .eq("id", header.id)
    .select("*")
    .single();
  if (updErr) throw updErr;
  return hydrateSo(updated as SalesOrder);
}

export function useCreateSalesOrder() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      values,
      confirm = false,
    }: {
      values: SalesOrderFormValues;
      confirm?: boolean;
    }) => insertSalesOrder(values, { status: confirm ? "confirmed" : "draft", userId: user?.id }),
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["sales_orders"] });
      toast.success(vars.confirm ? "Sales order created and confirmed" : "Sales order created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create sales order"),
  });
}

export function useUpdateSalesOrder() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
      replaceLines: doReplace,
      confirm = false,
    }: {
      id: string;
      values: SalesOrderFormValues;
      replaceLines: boolean;
      confirm?: boolean;
    }) => {
      const { data: existing, error: exErr } = await supabase
        .from("sales_orders")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const so = hydrateSo(existing as SalesOrder);
      if (so.status !== "draft" && so.status !== "confirmed") {
        throw new Error("Cannot edit sales order in this status");
      }

      const [client, company, rates] = await Promise.all([
        loadClientPos(values.client_id),
        loadCompanyState(),
        loadTaxRates(),
      ]);

      const patch: Record<string, unknown> = {
        client_id: values.client_id,
        quotation_id: text(values.quotation_id),
        client_po_ref: text(values.client_po_ref),
        issue_date: values.issue_date,
        due_date: text(values.due_date),
        bill_to_text: text(values.bill_to_text),
        ship_to_text: text(values.ship_to_text),
        payment_terms: text(values.payment_terms) ?? client.payment_terms,
        notes: text(values.notes),
        terms: text(values.terms),
        discount: values.discount,
        adjustment: values.adjustment,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (confirm && so.status === "draft") patch.status = "confirmed";

      let totals = {
        subtotal: so.subtotal,
        tax_total: so.tax_total,
        discount: values.discount,
        adjustment: values.adjustment,
        grand_total: so.grand_total,
      };

      if (doReplace) {
        const clientPos = client.place_of_supply || client.state;
        totals = await replaceLines(id, values, rates, company?.state, clientPos);
        Object.assign(patch, totals);
      }

      const { data: updated, error } = await supabase
        .from("sales_orders")
        .update(patch)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return hydrateSo(updated as SalesOrder);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["sales_orders"] });
      await queryClient.invalidateQueries({ queryKey: ["sales_orders", vars.id] });
      toast.success(vars.confirm ? "Sales order saved and confirmed" : "Sales order saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save sales order"),
  });
}

export function useUpdateSalesOrderStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: SalesOrderStatus }) => {
      const { data, error } = await supabase
        .from("sales_orders")
        .update({
          status,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return hydrateSo(data as SalesOrder);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["sales_orders"] });
      await queryClient.invalidateQueries({ queryKey: ["sales_orders", vars.id] });
      toast.success("Status updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function useDeleteSalesOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error: fetchErr } = await supabase
        .from("sales_orders")
        .select("status")
        .eq("id", id)
        .single();
      if (fetchErr) throw fetchErr;
      if ((data as { status: SalesOrderStatus }).status !== "draft") {
        throw new Error("Only draft sales orders can be deleted");
      }
      const { error } = await supabase.from("sales_orders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sales_orders"] });
      toast.success("Sales order deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete sales order"),
  });
}

export function useConvertQuotationToSalesOrder() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (quotationId: string) => {
      const { data: qt, error: qtErr } = await supabase
        .from("quotations")
        .select(
          `*,
          quotation_lines(
            *,
            quotation_line_taxes(*)
          )`,
        )
        .eq("id", quotationId)
        .single();
      if (qtErr) throw qtErr;

      const raw = qt as Record<string, unknown>;
      if (raw.status !== "accepted") {
        throw new Error("Only accepted quotations can be converted");
      }
      if (raw.sales_order_id) {
        throw new Error("Quotation already converted to a sales order");
      }

      const linesRaw = (raw.quotation_lines as Record<string, unknown>[] | null) ?? [];
      const values: SalesOrderFormValues = {
        client_id: raw.client_id as string,
        quotation_id: quotationId,
        client_po_ref: "",
        issue_date: (raw.issue_date as string) ?? new Date().toISOString().slice(0, 10),
        due_date: (raw.valid_until as string | null) ?? "",
        payment_terms: (raw.payment_terms as string | null) ?? "",
        bill_to_text: (raw.bill_to_text as string | null) ?? "",
        ship_to_text: (raw.ship_to_text as string | null) ?? "",
        discount: money(raw.discount),
        adjustment: money(raw.adjustment),
        notes: (raw.notes as string | null) ?? "",
        terms: (raw.terms as string | null) ?? "",
        lines: linesRaw
          .sort((a, b) => (a.line_no as number) - (b.line_no as number))
          .map((line) => ({
            material_id: line.material_id as string,
            material_grade_id: (line.material_grade_id as string | null) ?? "",
            shape_size_id: (line.shape_size_id as string | null) ?? "",
            description: (line.description as string | null) ?? "",
            qty: money(line.qty),
            weight: money(line.weight),
            uom_id: (line.uom_id as string | null) ?? "",
            unit_rate: money(line.unit_rate),
            gst_slab: money(line.gst_slab),
          })),
      };

      if (!values.lines.length) throw new Error("Quotation has no lines");

      const so = await insertSalesOrder(values, { status: "confirmed", userId: user?.id });

      const { error: linkErr } = await supabase
        .from("quotations")
        .update({
          status: "converted",
          sales_order_id: so.id,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", quotationId);
      if (linkErr) throw linkErr;

      return so;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sales_orders"] });
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      toast.success("Sales order created from quotation");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to convert quotation"),
  });
}
